import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";

const WEEKDAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function SlotSelect({
  month,
  year,
  minMonth,
  minYear,
  maxMonth,
  maxYear,
  availability,
  selectedDay,
  onMonthChange,
  onDaySelect,
  onSlotSelect,
  loading,
  error
}) {
  const safeAvailability = availability || {};
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const leadingEmpty = (firstWeekday + 6) % 7;
  const selectedSlots = selectedDay ? safeAvailability[selectedDay] || [] : [];
  const hasAvailability = Object.keys(safeAvailability).length > 0;
  const monthLabel = new Intl.DateTimeFormat("pt-PT", {
    month: "long",
    year: "numeric"
  }).format(new Date(year, month - 1, 1));
  const displayMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
  const canGoPrev = year > minYear || (year === minYear && month > minMonth);
  const canGoNext = year < maxYear || (year === maxYear && month < maxMonth);

  function formatMinutes(totalMinutes) {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  function handlePrevMonth() {
    if (!canGoPrev) return;
    let nextMonth = month - 1;
    let nextYear = year;
    if (nextMonth < 1) {
      nextMonth = 12;
      nextYear -= 1;
    }
    onMonthChange(nextMonth, nextYear);
  }

  function handleNextMonth() {
    if (!canGoNext) return;
    let nextMonth = month + 1;
    let nextYear = year;
    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }
    onMonthChange(nextMonth, nextYear);
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          type="button"
          onClick={handlePrevMonth}
          disabled={!canGoPrev}
          className="p-2 rounded-md hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h2 className="text-lg font-semibold text-white capitalize">
          {displayMonth}
        </h2>
        <button
          type="button"
          onClick={handleNextMonth}
          disabled={!canGoNext}
          className="p-2 rounded-md hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Próximo mês"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="text-center text-xs font-medium text-muted py-2"
          >
            {label}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-2 mb-6">
        {Array.from({ length: leadingEmpty }).map((_, index) => (
          <div key={`spacer-${index}`} aria-hidden="true" />
        ))}
        {Array.from({ length: daysInMonth }).map((_, index) => {
          const day = index + 1;
          const daySlots = safeAvailability[day] || [];
          const hasSlots = daySlots.length > 0;
          const isSelected = day === selectedDay;

          return (
            <button
              key={day}
              type="button"
              onClick={() => hasSlots && onDaySelect(day)}
              disabled={!hasSlots || loading}
              className={cn(
                "aspect-square rounded-md flex items-center justify-center text-sm font-medium transition-colors",
                hasSlots
                  ? "bg-background text-white hover:bg-gold/20 cursor-pointer"
                  : "text-muted/30 cursor-not-allowed",
                isSelected && "bg-gold text-white hover:bg-gold",
                loading && "opacity-50"
              )}
            >
              {day}
            </button>
          );
        })}
      </div>

      {/* Loading State */}
      {loading && (
        <p className="text-muted text-center py-4">A carregar dias disponíveis...</p>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* No Availability */}
      {!loading && !error && !hasAvailability && (
        <p className="text-muted text-center py-4">Sem horários disponíveis neste mês.</p>
      )}

      {/* Prompt to Select Day */}
      {!loading && hasAvailability && !selectedDay && (
        <p className="text-muted text-center py-4">Selecione um dia com horários disponíveis.</p>
      )}

      {/* Available Times */}
      {selectedDay && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-white font-semibold mb-4">Available Times</h3>
          {selectedSlots.length === 0 ? (
            <p className="text-muted text-center py-4">
              Sem horários disponíveis para este dia.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {selectedSlots.map((slot) => (
                <button
                  key={`${slot.start_minute}`}
                  type="button"
                  onClick={() => onSlotSelect(slot)}
                  className="px-4 py-2 bg-background text-white rounded-full text-sm font-medium hover:bg-gold hover:text-white transition-colors border border-border hover:border-gold"
                >
                  {formatMinutes(slot.start_minute)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
